import { postApi, putFileToS3 } from "./api";
import { getStringFromLocalStorage } from "./localStorage";

const apiUrl = import.meta.env.VITE_API_URL as string;

interface PresignedUrlResponse {
  presigned_url: string;
  bucket_name: string;
  file_key: string;
  expires_in: number;
}

interface TextractResponse {
  success: boolean;
  file_key: string;
  extracted_text: string[];
  recipe_data: {
    title: string;
    ingredients: string[];
    instructions: string[];
    prep_time: string;
    cook_time: string;
    servings: string;
  };
  text_block_count: number;
}

export class S3UploadService {
  private apiToken: string;

  constructor() {
    this.apiToken = getStringFromLocalStorage("idToken");
  }

  async getPresignedUrl(fileType: string): Promise<PresignedUrlResponse> {
    try {
      const response: any = await postApi(apiUrl, "presigned-url", {
        file_type: fileType
      }, this.apiToken);

      console.log("Raw presigned URL response:", response);
      const parsedResponse = JSON.parse(response);
      console.log("Parsed presigned URL response:", parsedResponse);

      return parsedResponse.message;
    } catch (error) {
      console.error("Error getting presigned URL:", error);
      throw new Error("Failed to get presigned URL for upload");
    }
  }

  async extractRecipeText(fileKey: string, bucketName?: string): Promise<TextractResponse> {
    try {
      const response: any = await postApi(apiUrl, "recipe-textract", {
        file_key: fileKey,
        bucket_name: bucketName
      }, this.apiToken);

      console.log("Raw Textract response:", response);
      const parsedResponse = JSON.parse(response);
      console.log("Parsed Textract response:", parsedResponse);

      return parsedResponse.message;
    } catch (error) {
      console.error("Error extracting recipe text:", error);
      throw new Error("Failed to extract recipe text from image");
    }
  }

  async uploadFile(file: File): Promise<string> {
    try {
      const presignedData = await this.getPresignedUrl(file.type);
      console.log("About to upload to URL:", presignedData.presigned_url);

      if (!presignedData.presigned_url) {
        throw new Error("No presigned URL received from API");
      }

      const uploadResponse = await putFileToS3(presignedData.presigned_url, file, file.type);

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error("S3 upload error response:", errorText);
        throw new Error(`Upload failed with status: ${uploadResponse.status} - ${errorText}`);
      }

      console.log("Upload successful, file key:", presignedData.file_key);
      return presignedData.file_key;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  }

  async uploadAndExtractRecipe(file: File): Promise<{ fileKey: string; recipeData: TextractResponse }> {
    try {
      // First upload the file
      const fileKey = await this.uploadFile(file);
      console.log("File uploaded successfully, starting text extraction...");

      // Then extract recipe text using Textract
      const recipeData = await this.extractRecipeText(fileKey);
      console.log("Recipe extraction completed:", recipeData);

      return { fileKey, recipeData };
    } catch (error) {
      console.error("Error in upload and extract process:", error);
      throw error;
    }
  }
}